import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import MarketingLayout from '../components/marketing/MarketingLayout';
import { useAuth } from '../context/AuthContext';
import { useStudyPlans } from '../hooks/useStudyPlans';
import { formatUsd } from '../data/studyPlans';
import { BRAND } from '../data/siteContent';

export default function CheckoutPage() {
  const { currentUser, isAdmin } = useAuth();
  const { plans, loading } = useStudyPlans();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const planFromQuery = searchParams.get('plan') || '';

  const [selectedId, setSelectedId] = useState(planFromQuery);

  useEffect(() => {
    if (planFromQuery) setSelectedId(planFromQuery);
  }, [planFromQuery]);

  useEffect(() => {
    if (!selectedId && plans.length) {
      const featured = plans.find((p) => p.featured) || plans[0];
      setSelectedId(featured.id);
    }
  }, [plans, selectedId]);

  const selected = useMemo(() => {
    if (!plans.length) return null;
    return plans.find((p) => p.id === selectedId) || plans[0];
  }, [plans, selectedId]);

  function continueCheckout() {
    if (!selected) return;
    if (isAdmin) {
      navigate('/admin');
      return;
    }
    if (currentUser) {
      navigate('/billing', { state: { planId: selected.id } });
      return;
    }
    navigate('/signup', { state: { planId: selected.id } });
  }

  return (
    <MarketingLayout>
      <header className="mk-page-hero">
        <div className="mk-container">
          <span className="mk-eyebrow">Checkout / Payment</span>
          <h1>Choose your plan and continue to payment</h1>
          <p>
            Select a {BRAND.name} study plan. Guests create an account next; signed-in students continue to the secure
            payment gate to unlock the portal.
          </p>
        </div>
      </header>

      <section className="mk-section">
        <div className="mk-container mk-checkout-layout">
          <div>
            <h2 className="mk-h2" style={{ marginBottom: 16 }}>
              Study plans
            </h2>
            {loading && !plans.length ? <p>Loading plans…</p> : null}
            <div className="mk-plan-pick">
              {plans.map((plan) => {
                const active = selected?.id === plan.id;
                return (
                  <button
                    key={plan.id}
                    type="button"
                    className={`mk-plan-pick-btn${active ? ' is-active' : ''}`}
                    onClick={() => setSelectedId(plan.id)}
                  >
                    <strong>
                      {plan.name}
                      {plan.badge ? ` · ${plan.badge}` : ''}
                    </strong>
                    <span>
                      {plan.durationLabel} — {formatUsd(plan.price)}
                      {plan.discountPercent > 0 ? ` (${plan.discountPercent}% off)` : ''}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mk-price-note" style={{ marginTop: 16 }}>
              By continuing you agree to our{' '}
              <Link to="/terms-and-conditions">Terms &amp; Conditions</Link> and{' '}
              <Link to="/refund-cancellation-policy">Refund &amp; Cancellation Policy</Link>.
            </p>
          </div>

          <aside className="mk-checkout-summary">
            <h2>Order summary</h2>
            {selected ? (
              <>
                <div className="mk-checkout-line">
                  <span>Plan</span>
                  <span>{selected.name}</span>
                </div>
                <div className="mk-checkout-line">
                  <span>Access</span>
                  <span>{selected.durationLabel}</span>
                </div>
                {selected.discountPercent > 0 ? (
                  <div className="mk-checkout-line">
                    <span>Regular price</span>
                    <span>{formatUsd(selected.regularPrice)}</span>
                  </div>
                ) : null}
                <div className="mk-checkout-total">
                  <span>Total due</span>
                  <span>{formatUsd(selected.price)}</span>
                </div>
                <button type="button" className="mk-btn mk-btn-primary mk-btn-block" style={{ marginTop: 20 }} onClick={continueCheckout}>
                  {currentUser && !isAdmin ? 'Continue to payment gate' : 'Create account & continue'}
                </button>
                <Link to="/pricing" className="mk-btn mk-btn-secondary mk-btn-block" style={{ marginTop: 10 }}>
                  Back to pricing
                </Link>
              </>
            ) : (
              <p>Select a plan to continue.</p>
            )}
          </aside>
        </div>
      </section>
    </MarketingLayout>
  );
}
